import React from 'react';
import type { IconType } from 'react-icons';
import {
  MdApi,
  MdBarChart,
  MdBiotech,
  MdCached,
  MdChevronLeft,
  MdChevronRight,
  MdCode,
  MdComputer,
  MdDataObject,
  MdDescription,
  MdDesignServices,
  MdGames,
  MdLanguage,
  MdMovie,
  MdMusicNote,
  MdPalette,
  MdPhoneAndroid,
  MdPool,
  MdPsychology,
  MdLandscape,
  MdPublic,
  MdRocketLaunch,
  MdScience,
  MdSmartToy,
  MdSportsEsports,
  MdSurfing,
  MdTrackChanges,
  MdTrendingUp,
  MdViewKanban,
} from 'react-icons/md';
import { DiIllustrator, DiPhotoshop } from 'react-icons/di';
import {
  SiAngular,
  SiClaude,
  SiCss,
  SiDocker,
  SiDotnet,
  SiFigma,
  SiFlutter,
  SiGit,
  SiHtml5,
  SiIonic,
  SiJavascript,
  SiJenkins,
  SiKubernetes,
  SiMongodb,
  SiN8N,
  SiNextdotjs,
  SiNodedotjs,
  SiOllama,
  SiOpenjdk,
  SiPython,
  SiReact,
  SiTypescript,
} from 'react-icons/si';
import { TbBrandAdobeXd, TbFileDescription, TbPlugConnected, TbSparkles, TbTerminal2 } from 'react-icons/tb';
import { ReactComponent as AntigravityMark } from '../../assets/icons/antigravity.svg';

/** Tamaño estándar para iconos en tarjetas y listas */
export const ICON_SM = 22;
export const ICON_MD = 26;
export const ICON_LG = 32;

/** Contenedor cuadrado fijo: alinea marcas de distintos sets (Material, Simple, Devicon, Tabler) */
function SkillIconSlot({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: ICON_MD,
        height: ICON_MD,
        flexShrink: 0,
        lineHeight: 0,
      }}
    >
      {children}
    </span>
  );
}

const skillIconSvgStyle: React.CSSProperties = {
  display: 'block',
  width: ICON_MD,
  height: ICON_MD,
  maxWidth: '100%',
  maxHeight: '100%',
};

function AntigravityIcon({ size = ICON_MD }: { size?: number }) {
  return (
    <AntigravityMark
      width={size}
      height={size}
      style={{ ...skillIconSvgStyle, width: size, height: size }}
      aria-hidden
    />
  );
}

export function renderSkillIcon(skillName: string): React.ReactNode {
  const skill = skillName.toLowerCase();

  let inner: React.ReactNode;

  if (skill.includes('antigravity')) {
    inner = <AntigravityIcon />;
  } else if (skill.includes('openclaw') || skill.includes('clawbot') || skill.includes('cloudbot')) {
    inner = <MdSmartToy style={skillIconSvgStyle} />;
  } else if (skill.includes('n8n')) {
    inner = <SiN8N style={skillIconSvgStyle} />;
  } else if (skill.includes('ollama')) {
    inner = <SiOllama style={skillIconSvgStyle} />;
  } else if (skill.includes('claude code') || skill === 'claude' || skill.includes('claude ai')) {
    inner = <SiClaude style={skillIconSvgStyle} />;
  } else if (skill.includes('mcp') || skill.includes('a2a')) {
    inner = <TbPlugConnected style={skillIconSvgStyle} />;
  } else if (skill === 'skills') {
    inner = <TbSparkles style={skillIconSvgStyle} />;
  } else if (skill.includes('scrum')) {
    inner = <MdViewKanban style={skillIconSvgStyle} />;
  } else if (skill.includes('cursor')) {
    inner = <TbTerminal2 style={skillIconSvgStyle} />;
  } else if (
    skill.includes('document') ||
    skill.includes('documentación') ||
    skill.includes('documentation') ||
    skill.includes('dokumentation')
  ) {
    inner = <TbFileDescription style={skillIconSvgStyle} />;
  } else if (skill.includes('javascript')) {
    inner = <SiJavascript style={skillIconSvgStyle} />;
  } else if (skill.includes('typescript')) {
    inner = <SiTypescript style={skillIconSvgStyle} />;
  } else if (skill.includes('css') || skill.includes('sass')) {
    inner = <SiCss style={skillIconSvgStyle} />;
  } else if (skill.includes('html')) {
    inner = <SiHtml5 style={skillIconSvgStyle} />;
  } else if (skill.includes('angular')) {
    inner = <SiAngular style={skillIconSvgStyle} />;
  } else if (skill.includes('native')) {
    inner = <SiReact style={skillIconSvgStyle} />;
  } else if (skill.includes('react')) {
    inner = <SiReact style={skillIconSvgStyle} />;
  } else if (skill.includes('ionic')) {
    inner = <SiIonic style={skillIconSvgStyle} />;
  } else if (skill.includes('flutter')) {
    inner = <SiFlutter style={skillIconSvgStyle} />;
  } else if (skill.includes('next')) {
    inner = <SiNextdotjs style={skillIconSvgStyle} />;
  } else if (skill.includes('java')) {
    inner = <SiOpenjdk style={skillIconSvgStyle} />;
  } else if (skill.includes('c#') || skill.includes('.net')) {
    inner = <SiDotnet style={skillIconSvgStyle} />;
  } else if (skill.includes('python')) {
    inner = <SiPython style={skillIconSvgStyle} />;
  } else if (skill.includes('api')) {
    inner = <MdApi style={skillIconSvgStyle} />;
  } else if (skill.includes('git')) {
    inner = <SiGit style={skillIconSvgStyle} />;
  } else if (skill.includes('illustrator')) {
    inner = <DiIllustrator style={skillIconSvgStyle} />;
  } else if (skill.includes('photoshop')) {
    inner = <DiPhotoshop style={skillIconSvgStyle} />;
  } else if (skill.includes('figma')) {
    inner = <SiFigma style={skillIconSvgStyle} />;
  } else if (skill.includes('xd') || skill.includes('adobe xd')) {
    inner = <TbBrandAdobeXd style={skillIconSvgStyle} />;
  } else if (skill.includes('ui/ux')) {
    inner = <MdDesignServices style={skillIconSvgStyle} />;
  } else if (skill.includes('docker')) {
    inner = <SiDocker style={skillIconSvgStyle} />;
  } else if (skill.includes('kubernetes') || skill.includes('k8s') || skill.includes('k8')) {
    inner = <SiKubernetes style={skillIconSvgStyle} />;
  } else if (skill.includes('ci/cd') || skill.includes('cicd')) {
    inner = <MdCached style={skillIconSvgStyle} />;
  } else if (skill.includes('jenkins')) {
    inner = <SiJenkins style={skillIconSvgStyle} />;
  } else if (skill.includes('mongodb')) {
    inner = <SiMongodb style={skillIconSvgStyle} />;
  } else if (skill.includes('node')) {
    inner = <SiNodedotjs style={skillIconSvgStyle} />;
  } else {
    inner = <MdComputer style={skillIconSvgStyle} />;
  }

  return <SkillIconSlot>{inner}</SkillIconSlot>;
}

const interestIcons: IconType[] = [
  MdMusicNote,
  MdSportsEsports,
  MdComputer,
  MdScience,
  MdSurfing,
  MdPool,
  MdLandscape,
  MdMovie,
  MdScience,
  MdBiotech,
  MdCode,
  MdSmartToy,
  MdDataObject,
  MdPublic,
  MdPhoneAndroid,
  MdPalette,
  MdBarChart,
  MdTrackChanges,
  MdSmartToy,
  MdGames,
];

export function renderInterestIcon(index: number): React.ReactNode {
  const Icon = interestIcons[Math.min(index, interestIcons.length - 1)];
  return <Icon size={ICON_SM} style={{ display: 'block' }} />;
}

export function renderLanguagePrefix(): React.ReactNode {
  return <MdLanguage size={18} style={{ display: 'block', marginRight: 6, opacity: 0.85 }} aria-hidden />;
}

/** Iconos Material para las 8 fases Scrum (orden fijo) */
export const scrumPhaseIcons: IconType[] = [
  MdTrackChanges,
  MdDescription,
  MdComputer,
  MdBarChart,
  MdBiotech,
  MdTrendingUp,
  MdPsychology,
  MdRocketLaunch,
];

export function ScrumPhaseIcon({
  index,
  size = ICON_LG,
  color = 'rgba(255,255,255,0.95)',
}: {
  index: number;
  size?: number;
  /** Usa `currentColor` en chips o botones para heredar el color del texto */
  color?: string;
}) {
  const Icon = scrumPhaseIcons[Math.min(index, scrumPhaseIcons.length - 1)];
  return <Icon size={size} style={{ display: 'block', color }} />;
}

export const ChevronLeft = MdChevronLeft;
export const ChevronRight = MdChevronRight;
